import { CheckoutContext } from '@/context/CheckoutContext';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { StripePaymentIntentStatus } from '@/types/payment';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { getEventDataForSdkEvents } from '@/utils/eventDataBuilder';
import { notifyPaymentIntentFailed, notifyPaymentIntentSuccess } from '@/utils/notifier';
import { useCheckoutData } from '@/api/useCheckoutData';
import { stripeService } from '@/services/stripe';
import { getAppchargeErrorMessage } from '@/utils/error';
import { getStripeDeclineReason } from '@/utils/stripe';

export const CashAppRedirect = () => {
  const isInProcessing = useRef(false);
  const context = useContext(CheckoutContext);
  const checkoutData = useCheckoutData({ enableFetch: false });
  const {
    stripe,
    redirectPayment,
    actions: { setError, startOrderPolling, setSuccessScreen, setIsInPaymentProcess, setRedirectPayment },
  } = context;
  const { clientSecret } = redirectPayment;
  const { t } = useTranslate();
  const handleCashAppNextAction = useCallback(async () => {
    if (isInProcessing.current) return;
    isInProcessing.current = true;

    let stripeForNextAction = stripe;
    try {
      // StripeGenericPayForm registers the Cash App / PIX instance. The shared
      // context instance is the currency account.
      stripeForNextAction = stripeService.getStripe();
    } catch {
      // Form has not registered yet. Keep the shared instance.
    }

    try {
      const paymentIntentResult = await stripeForNextAction?.handleNextAction({ clientSecret: clientSecret || '' });
      const paymentIntentStatus = paymentIntentResult?.paymentIntent?.status;
      const lastPaymentError = paymentIntentResult?.paymentIntent?.last_payment_error;

      // A real decline: either Stripe returned an error directly, or the intent
      // bounced back to `requires_payment_method` (the issuer rejected it).
      // Mirrors `StripePaymentHandler.poll`'s redirect-tab mapping so in-page
      // and tab-based flows report the same normalized decline type.
      if (paymentIntentResult?.error || paymentIntentStatus === StripePaymentIntentStatus.REQUIRES_PAYMENT_METHOD) {
        const declineReason = getStripeDeclineReason(paymentIntentResult?.error, lastPaymentError);
        const appChargeErrorType = stripeService.mapStripeErrorCode(declineReason);
        setError(getAppchargeErrorMessage(t, appChargeErrorType));
        notifyPaymentIntentFailed(checkoutData.data!, PaymentMethodEnum.CASH_APP, {
          reason: paymentIntentResult?.error?.message,
        });
        setIsInPaymentProcess(false);
        setRedirectPayment({});
        isInProcessing.current = false;
      } else if (paymentIntentStatus === StripePaymentIntentStatus.REQUIRES_ACTION) {
        // Shopper dismissed the in-page action without completing it (e.g.
        // closed the Cash App prompt) and Stripe reports no error: this is a
        // cancel/abandon, not a decline. Per ACDEV-25591 it must not surface
        // an error or notify the SDK of a payment failure.
        setIsInPaymentProcess(false);
        setRedirectPayment({});
        isInProcessing.current = false;
      }
      if (paymentIntentStatus === StripePaymentIntentStatus.SUCCEEDED) {
        startOrderPolling(redirectPayment.orderId || '');
        notifyPaymentIntentSuccess(
          getEventDataForSdkEvents(context, { paymentMethodName: PaymentMethodEnum.CASH_APP })
        );
        setSuccessScreen({
          isOpen: true,
          paymentDetails: { params: paymentIntentResult?.paymentIntent },
          provider: ProviderEnum.STRIPE,
        });
      }
    } catch (error) {
      setError(t('somethingWentWrong'));
      setIsInPaymentProcess(false);
      setRedirectPayment({});
      isInProcessing.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret]);

  useEffect(() => {
    handleCashAppNextAction();
  }, [handleCashAppNextAction]);

  return null;
};
